import type { ReactNode } from 'react';
import {
  Body,
  Caption,
  Dialog,
  DialogSection,
  PersonRowSkeleton,
  SkeletonGroup,
  Stack,
  TabBar,
  type TabBarItem,
} from '@leanin/ui';
import { MemberRow } from '../MemberRow';
import { InlineStatus } from '../InlineStatus';
import { LoadMore } from '../pagination/index';
import { errorMessage } from '../../lib/error-message';

export type PeopleListTab = {
  readonly id: string;
  readonly label: string;
  readonly count: number;
};

export type PeopleListPerson = {
  /** Unique within the list — a person can appear once per tab slice. */
  readonly key: string;
  readonly userId: string;
  readonly name: string;
  readonly photoUrl: string | null;
  readonly sublabel: string | null;
  /** Trailing per-row marker, e.g. the reaction the member left. */
  readonly right?: ReactNode;
};

// The slice of a `useInfiniteQuery` result the list reads. Structural, so any
// cursor-paged `{ items }` roster query plugs in unchanged.
export type PeopleListQuery<T> = {
  readonly data:
    | { readonly pages: ReadonlyArray<{ readonly items: ReadonlyArray<T> }> }
    | undefined;
  readonly error: unknown;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => Promise<unknown>;
};

type Props<T> = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly tabs: ReadonlyArray<PeopleListTab>;
  readonly activeTab: string;
  readonly onTabChange: (id: string) => void;
  /** The roster for `activeTab`. */
  readonly query: PeopleListQuery<T>;
  readonly toPerson: (item: T) => PeopleListPerson;
  readonly loadingLabel: string;
  readonly emptyLabel: string;
  /** Copy for people a tab counts but the roster does not list (e.g. poll
   *  votes from accounts that are no longer active). Shown once the roster is
   *  fully loaded and shorter than the active tab's count. */
  readonly unlistedLabel?: (count: number) => string;
};

// "Who did X" sheet: a tab strip with per-tab counts over one cursor-paged
// member roster (reactors, poll voters). The caller owns the tab state
// because the active tab selects which query feeds the list.
export function PeopleListModal<T>({
  open,
  onOpenChange,
  title,
  tabs,
  activeTab,
  onTabChange,
  query,
  toPerson,
  loadingLabel,
  emptyLabel,
  unlistedLabel,
}: Props<T>) {
  const items: TabBarItem[] = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
    count: tab.count,
  }));
  const people = query.data?.pages.flatMap((p) => p.items.map(toPerson)) ?? null;
  const activeCount = tabs.find((tab) => tab.id === activeTab)?.count ?? 0;
  const unlisted =
    people !== null && !query.hasNextPage && query.error === null
      ? activeCount - people.length
      : 0;
  const unlistedNote =
    unlistedLabel !== undefined && unlisted > 0 ? (
      <Caption>{unlistedLabel(unlisted)}</Caption>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} size="sm">
      {/* Tabs share the list's section — as a loose child the TabBar gets
          its own padded band, which reads as a double gap above row one. */}
      <DialogSection scrollable gap="md">
        <TabBar
          items={items}
          value={activeTab}
          onChange={onTabChange}
          ariaLabel={title}
        />
        {people === null ? (
          query.error !== null ? (
            <InlineStatus>{errorMessage(query.error)}</InlineStatus>
          ) : (
            <SkeletonGroup label={loadingLabel}>
              <Stack gap="md">
                <PersonRowSkeleton avatarSize={36} />
                <PersonRowSkeleton avatarSize={36} />
                <PersonRowSkeleton avatarSize={36} />
              </Stack>
            </SkeletonGroup>
          )
        ) : people.length === 0 ? (
          <Stack gap="xs">
            <Body color="mute">{emptyLabel}</Body>
            {unlistedNote}
          </Stack>
        ) : (
          <Stack gap="md">
            {people.map((person) => (
              <MemberRow
                key={person.key}
                userId={person.userId}
                {...(person.photoUrl !== null ? { photo: person.photoUrl } : {})}
                name={person.name}
                {...(person.sublabel !== null ? { sublabel: person.sublabel } : {})}
                avatarSize={36}
                right={person.right}
              />
            ))}
            {query.error !== null ? (
              <InlineStatus>{errorMessage(query.error)}</InlineStatus>
            ) : null}
            {unlistedNote}
            <LoadMore
              hasMore={query.hasNextPage}
              isLoading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
              size="sm"
            />
          </Stack>
        )}
      </DialogSection>
    </Dialog>
  );
}
